import { SUPPORTED_LOCALES } from '@platform/i18n';
import { useTranslation } from 'react-i18next';
import { setPreferredLocale } from '../api.ts';
import { changeLocale } from '../i18n.ts';

/**
 * Scelta della lingua della console.
 *
 * È una `<select>` con una `<label>` vera: un menu costruito a mano sarebbe da
 * rendere accessibile daccapo, e questo non è il posto in cui spendere quel
 * lavoro.
 */
export function LocalePicker({
  /**
   * C'è qualcuno che ha fatto accesso?
   *
   * Questo selettore sta anche nella schermata di accesso e in quella di un
   * invito, dove un profilo su cui salvare non esiste ancora: lì la scelta
   * vale per il dispositivo e basta. Chiedere al server in quei due punti
   * vorrebbe dire un 401 a ogni cambio di lingua.
   */
  signedIn = false,
}: {
  signedIn?: boolean;
}) {
  const { t, i18n } = useTranslation();

  /**
   * Prima il dispositivo, poi il profilo.
   *
   * `changeLocale` è quello che si vede: cambia la lingua a schermo e la
   * ricorda qui. La scrittura sul profilo è per il **dispositivo successivo**
   * (AS6), e se non riesce non deve togliere niente a chi ha appena scelto —
   * la lingua è cambiata comunque, e un messaggio d'errore per una preferenza
   * che ha funzionato sarebbe rumore.
   */
  const choose = async (locale: string): Promise<void> => {
    await changeLocale(locale);
    if (signedIn) await setPreferredLocale(locale).catch(() => undefined);
  };

  return (
    <div className="field field--inline">
      <label htmlFor="locale">{t('language.label')}</label>
      <select
        id="locale"
        value={i18n.resolvedLanguage}
        onChange={(event) => void choose(event.target.value)}
      >
        {SUPPORTED_LOCALES.map((locale) => (
          <option key={locale} value={locale}>
            {t(`language.${locale}`)}
          </option>
        ))}
      </select>
    </div>
  );
}
